import axios from 'axios';
import { auth } from '@/lib/firebase';
import type { ReviewUnit } from '@/lib/mlReviewUnits';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export type PanelScript = { script_id: string; title: string };
export type PanelModel = { slug: string; wave: string; blurb: string };
export type CellStatus = 'pending' | 'in_progress' | 'gold' | 'missing';

export type PanelResponse = {
  panel: { scripts: PanelScript[]; models: PanelModel[] };
  progress: Record<string, unknown>;
  cells: { script_id: string; model: string; status: CellStatus }[];
};

export async function getReviewPanel(): Promise<PanelResponse> {
  const { data } = await api.get('/api/ml-review/panel');
  if (data.status !== 'success') throw new Error('Failed to load panel');
  return data;
}

export async function getScriptText(scriptId: string): Promise<{ text: string; title: string; length: number }> {
  const { data } = await api.get(`/api/ml-review/scripts/${encodeURIComponent(scriptId)}/text`);
  if (data.status !== 'success') throw new Error('Failed to load script');
  return { text: data.text || '', title: data.title || scriptId, length: data.length || 0 };
}

export async function getReview(
  model: string,
  scriptId: string,
): Promise<{ review: Record<string, unknown>; units: ReviewUnit[]; progress: Record<string, unknown> }> {
  const { data } = await api.get(
    `/api/ml-review/${encodeURIComponent(model)}/${encodeURIComponent(scriptId)}`,
  );
  if (data.status !== 'success') throw new Error('Failed to load review');
  return { review: data.review, units: data.units || [], progress: data.progress || {} };
}

export type UndoFrame = {
  kind: 'accept' | 'skip' | 'delete' | 'add' | 'edit' | 'merge';
  unit_index: number;
  item_index: number;
  list_key?: string | null;
  item?: unknown;
  items?: unknown[];
  scalar_key?: string;
  scalar_value?: unknown;
  skipped_needles?: string[];
};

export type CheckpointBody = {
  action: 'accept' | 'edit' | 'delete' | 'add' | 'skip' | 'cursor' | 'merge' | 'undo';
  unit_index: number;
  list_key?: string | null;
  item_index: number;
  item?: unknown;
  scalar_key?: string;
  scalar_value?: unknown;
  note?: string;
  merge_names?: string[];
  interaction_count?: number;
  undo_frame?: UndoFrame;
};

export async function checkpointReview(
  model: string,
  scriptId: string,
  body: CheckpointBody,
): Promise<{
  review: Record<string, unknown>;
  units: ReviewUnit[];
  item_index?: number;
  unit_index?: number;
  undo_depth?: number;
  undone?: UndoFrame | null;
}> {
  const { data } = await api.patch(
    `/api/ml-review/${encodeURIComponent(model)}/${encodeURIComponent(scriptId)}/checkpoint`,
    body,
  );
  if (data.status !== 'success') throw new Error('Failed to save checkpoint');
  return {
    review: data.review,
    units: data.units || [],
    item_index: data.item_index,
    unit_index: data.unit_index,
    undo_depth: data.undo_depth,
    undone: data.undone || null,
  };
}

export async function finalizeReview(
  model: string,
  scriptId: string,
  opts?: { edited?: boolean; recompute_emotions?: boolean },
): Promise<{ review: Record<string, unknown> }> {
  const { data } = await api.post(
    `/api/ml-review/${encodeURIComponent(model)}/${encodeURIComponent(scriptId)}/finalize`,
    opts || {},
  );
  if (data.status !== 'success') throw new Error('Failed to finalize');
  return { review: data.review };
}
